'use client';

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/icons/svg-icons';
import { Modal } from '@/components/widgets/modal';
import { useTranslations } from 'next-intl';
import { usePromotionVouchers, useMarkFreeBetNotificationRead } from '@/hooks/query/use-reward-system';
import { useAccountInfo } from '@/hooks/query/account';
import { useEnableFreeBetVoucher } from '@/features/betslip/voucher/contexts/voucher-context';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

import { VoucherNotificationData, VoucherNotificationDetailInfo, VoucherReasonType, VoucherType } from '@nf/achievement-system-v2';
import styles from './free-bet-modal.module.scss';
import { KafkaNote } from '@nf/utils-common/constants';

const VoucherGroup = ({ 
	voucherGroup,
	currencyName,
	t
}: { 
	voucherGroup: VoucherNotificationData,
	currencyName: string,
	t: any
}) => {
	const voucherTitleMapping = {
		[VoucherType.FreeBet]: 'lbl_FBName',
	};

	const getVoucherTitle = () => {
		const resourceKey = voucherTitleMapping[voucherGroup.voucherNotificationInfo.voucherType];
		return resourceKey ? t(resourceKey) : '';
	};

	const isCustomized = voucherGroup.reason === VoucherReasonType.FreeBetCustomized;

	return (
	<div className={styles.voucher}>
		<div className={styles.voucherIconWrap}>
			<div className={styles.voucherIcon}>
				<span className={clsx(styles.voucherIconGift, isCustomized && styles.voucherIconGiftCustomized)} />
				<i className={styles.voucherIconFreeBet} />
				<span className={styles.countBadge}>x{voucherGroup.voucherNotificationInfo.total}</span>
			</div>
		</div>
		<div className={styles.voucherContent}>
			<div className={styles.voucherDivider} />
			<div className={styles.voucherContentBody}>
				<div className={styles.voucherTitle}>{getVoucherTitle()}</div>
				<div className={styles.voucherTags}>
					{voucherGroup.voucherNotificationInfo.voucherNotificationDetailInfo?.map((detail: VoucherNotificationDetailInfo) => (
						<span key={detail.definitionId} className={styles.voucherTag}>
							<span className={styles.voucherTagAmount}>{currencyName} {detail.amount} </span>
							<span className={styles.voucherTagQuantity}>x{detail.subTotal}</span>
						</span>
					))}
				</div>
			</div>
		</div>
	</div>
	);
};

export const FreeBetModal = () => {
	const t = useTranslations('General');
	const [isOpen, setIsOpen] = useState(false);
	const { data: voucherResponse, isLoading, isError } = usePromotionVouchers();
	const { user } = useAccountInfo();
	const currencyName = user?.LicenseeSiteSetting?.DisplayCurrencyName || user?.Curr || '';
	const enableFreeBetVoucher = useEnableFreeBetVoucher();
	const markFreeBetNotificationRead = useMarkFreeBetNotificationRead();
	
	const voucherData = voucherResponse?.voucherInfos || [];
	const notificationId = voucherResponse?.notificationId || 0;
	const promotionSuiteNotificationId = voucherResponse?.promotionSuiteNotificationId || null;

	useEffect(() => {
		if (!isLoading && voucherData && voucherData.length > 0 && !isError) {
			setIsOpen(true);
		}
	}, [voucherData, isLoading, isError]);

	if (!enableFreeBetVoucher) {
		return null;
	}

	const closePopup = async () => {
		setKafkaNormal({
			note: KafkaNote.Reward,
			type: 11,
			action: 1
		});
		markFreeBetNotificationRead(notificationId, promotionSuiteNotificationId);
		setIsOpen(false);
	};

	const goToMyVouchers = () => {
		setKafkaNormal({
			note: KafkaNote.Reward,
			type: 11,
			action: 2
		});
		markFreeBetNotificationRead(notificationId, promotionSuiteNotificationId);
		setIsOpen(false);
		document.dispatchEvent(new CustomEvent('open-vouchers'));
	};

	if (isLoading || isError) {
		return null;
	}

	if (!voucherData || voucherData.length === 0) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			variant="event"
			closeModal={closePopup}
			showHeader={false}
			className={styles.promoModal}
			isShowClose={true}
			isBackdropClose={false}
		>
			<div className={styles.container}>
				<div className={styles.pack}>
					<div className={styles.packWrapper}>
						<div className={styles.packHead}>{t('lbl_FBPopupInfo')}</div>
						<div className={styles.packBody}>
							<div className={styles.packScene1}>
								<div className={styles.packBox}>
									<div className={styles.packBoxBoxes} />
									<div className={styles.packBoxMain} />
									<div className={styles.packBoxLid} />
								</div>
								<div className={styles.packLight} />
							</div>
							<div className={styles.packScene2}>
								<div className={styles.packBoxOpened}>
									<div className={styles.packBoxOpenedRibbon}>
										<div
											className={clsx(
												styles.packBoxOpenedRibbonCorner, styles.packBoxOpenedRibbonCornerA,
											)}
										/>
										<div
											className={clsx(
												styles.packBoxOpenedRibbonCorner, styles.packBoxOpenedRibbonCornerB,
											)}
										/>
										<div
											className={clsx(
												styles.packBoxOpenedRibbonCorner, styles.packBoxOpenedRibbonCornerC,
											)}
										/>
										<div
											className={clsx(
												styles.packBoxOpenedRibbonCorner, styles.packBoxOpenedRibbonCornerD,
											)}
										/>
									</div>
								</div>
								<div className={styles.voucherListLabel} />
								<div className={styles.voucherList}>
									<div className={styles.voucherListScroll}>
										{voucherData.map((voucherGroup: VoucherNotificationData) => (
											<div key={voucherGroup.promotionId} className={styles.voucherGroup}>
												<div className={styles.voucherGroupHeader}>
													<Icon name="gift" className={styles.voucherGroupGiftIcon} />
													<span className={styles.voucherGroupName}>
														{voucherGroup.promotionResourceName}
													</span>
												</div>
												<VoucherGroup voucherGroup={voucherGroup} currencyName={currencyName} t={t} />
											</div>
										))}
									</div>
									<div className={styles.voucherListFooter}>
										<button
											type="button"
											className={clsx(styles.footerBtn, styles.footerBtnSecondary)}
											onClick={closePopup}
										>
											<span className={styles.multiLineEllipsis}>{t('lbl_FBPopupBtn')}</span>
										</button>
										<button
											type="button"
											className={clsx(styles.footerBtn, styles.footerBtnPrimary)}
											onClick={goToMyVouchers}
										>
											<span className={styles.multiLineEllipsis}>{t('lbl_obp03')}</span>
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
